<template>
  <div class="donation-container" v-if="showDonationScreen">
    <h1 class="donation-title">We're accepting contributions!</h1>
    <div class="donation-text">
      <p>
        Hey there! I'm really excited about a project that's all about fostering deeper connections
        through meaningful conversations, and your support could make a big difference.
      </p>
      <p>
        Every contribution, no matter how small, helps me get this app off the ground,
        so if you feel inspired, please consider clicking the link below to contribute
        or share it with others who might be interested.
      </p>
      <p>Thank you so much!</p>
    </div>
    <div class="donation-buttons">
      <div class="tip-options">
        <button 
          v-for="tip in tipOptions"
          :key="tip.id"
          class="donation-button-style" 
          :class="{ 'tip-purchased': isProductOwned[tip.id] }"
          :disabled="isPurchasing[tip.id] || isLoading || isProductOwned[tip.id]"
          @click="handleDonate(tip.id)"
        >
          <span v-if="isPurchasing[tip.id]">Processing...</span>
          <span v-else-if="isLoading">Loading...</span>
          <span v-else-if="isProductOwned[tip.id]">Thank you so much!</span>
          <span v-else>
            <!-- Display price if available, otherwise fallback to generic label -->
            <span v-if="tip.price">{{ tip.price }}</span>
            <span v-else>{{ tip.label }}</span>
          </span>
        </button>
      </div>
      <div v-if="purchaseError" class="purchase-error">
        {{ purchaseError }}
      </div>
      <div v-if="restoreMessage" class="restore-message" :class="{ 'restore-success': restoreSuccess, 'restore-error': !restoreSuccess }">
        {{ restoreMessage }}
      </div>
      <button 
        v-if="isNative && inAppPurchaseService.isAvailable()"
        class="restore-button" 
        :disabled="isRestoring || Object.values(isPurchasing).some(p => p) || isLoading"
        @click="handleRestorePurchases"
      >
        <span v-if="isRestoring">Restoring...</span>
        <span v-else>Restore Purchases</span>
      </button>
      <button class="donation-close-button" @click="closeDonationScreen">
        Close
      </button>
    </div>
  </div>
</template>

<script>
import { useStore } from '../store';
import { mapState, mapActions } from 'pinia';
import { Capacitor } from '@capacitor/core';
import { inAppPurchaseService } from '../services/inAppPurchaseService';
import { trackEvent } from '../services/analyticsService';

const PRODUCT_IDS = ['tip_0', 'tip_1', 'tip_2'];

export default {
  data() {
    return {
      isPurchasing: {
        tip_0: false,
        tip_1: false,
        tip_2: false,
      },
      isLoading: false,
      purchaseError: null,
      isNative: Capacitor.isNativePlatform(),
      isRestoring: false,
      restoreMessage: null,
      restoreSuccess: false,
      isProductOwned: {
        tip_0: false,
        tip_1: false,
        tip_2: false,
      },
      products: {
        tip_0: null,
        tip_1: null,
        tip_2: null,
      },
    };
  },
  computed: {
    ...mapState(useStore, ['showDonationScreen']),
    inAppPurchaseService() {
      return inAppPurchaseService;
    },
    tipOptions() {
      return PRODUCT_IDS.map(id => {
        const product = this.products[id];
        let displayPrice = null;

        if (product) {
          // 1. Try 'localizedPrice' (Standard StoreKit format, e.g., "$0.99")
          if (product.localizedPrice) {
            displayPrice = product.localizedPrice;
          } 
          // 2. Try 'price' if it's a string (Many Capacitor plugins return formatted string here)
          else if (typeof product.price === 'string' && product.price.match(/\d/)) {
            displayPrice = product.price;
          }
          // 3. Fallback: If 'price' is a number, combine with currency
          else if (typeof product.price === 'number') {
            const symbol = product.currency ? this.getCurrencySymbol(product.currency) : '';
            // Simple format if raw number
            displayPrice = `${symbol}${product.price}`;
          }
          // 4. Try specific 'priceAsString' (used by some plugins)
          else if (product.priceAsString) {
            displayPrice = product.priceAsString;
          }
        }

        return {
          id,
          label: id === 'tip_0' ? 'Tip 1' : id === 'tip_1' ? 'Tip 2' : 'Tip 3',
          price: displayPrice,
        };
      });
    },
  },
  methods: {
    ...mapActions(useStore, ['closeDonationScreen']),
    async handleDonate(productId) {
      this.purchaseError = null;
      
      if (this.isNative && inAppPurchaseService.isAvailable()) {
        await this.handleInAppPurchase(productId);
      } else {
        this.purchaseError = 'In-app purchases are not available on this platform.';
        trackEvent('donation_failed', {
          platform: Capacitor.getPlatform(),
          method: 'in_app_purchase',
          productId: productId,
          error: 'IAP not available'
        });
      }
    },
    async handleInAppPurchase(productId) {
      try {
        this.isLoading = true;
        
        // Refresh products just in case
        await this.loadProducts();
        
        this.isLoading = false;
        this.isPurchasing[productId] = true;
        
        const result = await inAppPurchaseService.purchase(productId);
        
        trackEvent('donation_completed', {
          platform: Capacitor.getPlatform(),
          method: 'in_app_purchase',
          productId: productId,
          transactionId: result.transactionId
        });
        
        this.purchaseError = null;
        alert('Thank you for your donation!');
        
        await this.checkProductOwnership();
        
        this.isPurchasing[productId] = false;
      } catch (error) {
        console.error('Purchase error:', error);
        this.purchaseError = error.message || 'Purchase failed. Please try again.';
        this.isPurchasing[productId] = false;
        this.isLoading = false;
        
        trackEvent('donation_failed', {
          platform: Capacitor.getPlatform(),
          method: 'in_app_purchase',
          productId: productId,
          error: error.message
        });
      }
    },
    async handleRestorePurchases() {
      this.restoreMessage = null;
      this.restoreSuccess = false;
      
      if (!this.isNative || !inAppPurchaseService.isAvailable()) {
        this.restoreMessage = 'Restore purchases is only available on native platforms';
        this.restoreSuccess = false;
        return;
      }
      
      try {
        this.isRestoring = true;
        
        const result = await inAppPurchaseService.restorePurchases();
        
        trackEvent('purchases_restored', {
          platform: Capacitor.getPlatform(),
          restoredCount: result.restoredProducts?.length || 0
        });
        
        if (result.restoredProducts && result.restoredProducts.length > 0) {
          this.restoreMessage = `Successfully restored ${result.restoredProducts.length} purchase(s)`;
          this.restoreSuccess = true;
          await this.checkProductOwnership();
        } else {
          this.restoreMessage = 'No previous purchases found to restore';
          this.restoreSuccess = false;
        }
        
        this.isRestoring = false;
      } catch (error) {
        console.error('Restore purchases error:', error);
        this.restoreMessage = error.message || 'Failed to restore purchases. Please try again.';
        this.restoreSuccess = false;
        this.isRestoring = false;
        
        trackEvent('restore_purchases_failed', {
          platform: Capacitor.getPlatform(),
          error: error.message
        });
      }
    },
    async checkProductOwnership() {
      if (this.isNative && inAppPurchaseService.isAvailable()) {
        try {
          for (const productId of PRODUCT_IDS) {
            this.isProductOwned[productId] = await inAppPurchaseService.isProductOwned(productId);
          }
        } catch (error) {
          console.error('Failed to check product ownership:', error);
        }
      }
    },
    async loadProducts() {
      if (inAppPurchaseService.isAvailable()) {
        try {
          this.isLoading = true;
          const result = await inAppPurchaseService.loadProducts(PRODUCT_IDS);
          
          if (result && result.products) {
            console.log('Raw Store Products:', JSON.stringify(result.products)); // Debugging line
            
            // Create a copy of the current products state
            const updatedProducts = { ...this.products };
            
            result.products.forEach(product => {
              if (product && product.productId) {
                updatedProducts[product.productId] = product;
              }
            });
            
            // Reassign the entire object to trigger Vue reactivity
            this.products = updatedProducts;
          }
          this.isLoading = false;
        } catch (error) {
          console.error('Failed to load products:', error);
          this.isLoading = false;
        }
      } else {
        this.isLoading = false;
      }
    },
    getCurrencySymbol(currencyCode) {
      const symbols = {
        'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'CAD': 'C$',
        'AUD': 'A$', 'CHF': 'CHF', 'CNY': '¥', 'INR': '₹', 'BRL': 'R$',
        'MXN': '$', 'KRW': '₩', 'SGD': 'S$', 'HKD': 'HK$', 'NZD': 'NZ$',
        'ZAR': 'R', 'SEK': 'kr', 'NOK': 'kr', 'DKK': 'kr', 'PLN': 'zł',
        'RUB': '₽', 'TRY': '₺',
      };
      return symbols[currencyCode] || currencyCode;
    },
  },
  async mounted() {
    if (this.showDonationScreen) {
      try {
        await this.loadProducts();
        if (this.isNative && inAppPurchaseService.isAvailable()) {
          await this.checkProductOwnership();
        }
      } catch (error) {
        console.error('Error in mounted:', error);
      }
    }
  },
  watch: {
    showDonationScreen(newValue) {
      if (newValue) {
        this.loadProducts().then(() => {
          if (this.isNative && inAppPurchaseService.isAvailable()) {
            this.checkProductOwnership();
          }
        }).catch(err => {
          console.error('Error in watcher:', err);
        });
      }
    }
  }
};
</script>

<style scoped>
.donation-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 40px 20px;
    text-align: center;
}

.donation-title {
    font-size: 2.5em;
    margin-top: 40px;
    margin-bottom: 30px;
    text-shadow: 0.5px 0 0 currentColor, 1px 0 0 currentColor, 1.5px 0 0 currentColor;
}

.donation-text {
    max-width: 600px;
    margin: 0 auto 40px;
    text-align: center;
    line-height: 1.6;
    font-size: 1.1em;
}

.donation-text p {
    margin-bottom: 20px;
}

.donation-buttons {
    display: flex;
    flex-direction: column;
    gap: 20px;
    align-items: center;
    margin-top: 30px;
}

.tip-options {
    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;
    width: 100%;
    max-width: 400px;
}

.donation-button-style {
    background: linear-gradient(to bottom right, #FFD700, #FDB900);
    color: #333;
    font-size: 1.3em;
    text-shadow: 0.5px 0 0 currentColor, 1px 0 0 currentColor, 1.5px 0 0 currentColor;
    padding: 15px 40px;
    border-radius: 25px;
    border: none;
    cursor: pointer;
    transition: transform 0.2s ease;
    text-decoration: none;
    display: inline-block;
}

.donation-button-style:active {
    transform: scale(0.95);
}

.donation-button-style:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #ccc;
    color: #666;
    text-shadow: none;
}

.donation-button-style.tip-purchased {
    background: #e0e0e0;
    color: #999;
    opacity: 0.7;
    cursor: not-allowed;
}

body.dark-mode .donation-button-style.tip-purchased {
    background: #444;
    color: #777;
}

.purchase-error {
    color: #ff4444;
    font-size: 0.9em;
    margin-top: 10px;
    padding: 10px;
    background-color: rgba(255, 68, 68, 0.1);
    border-radius: 8px;
    max-width: 400px;
}

body.dark-mode .purchase-error {
    color: #ff6666;
    background-color: rgba(255, 68, 68, 0.2);
}

.donation-close-button {
    background-color: #f0f0f0;
    color: #333;
    border: none;
    padding: 12px 24px;
    border-radius: 25px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.donation-close-button:active {
    background-color: #e0e0e0;
}

/* Dark mode styles for donation page */
body.dark-mode .donation-title {
    color: var(--peach);
}

body.dark-mode .donation-text {
    color: var(--text-dark);
}

body.dark-mode .donation-close-button {
    background-color: #333;
    color: white;
}

body.dark-mode .donation-close-button:active {
    background-color: #444;
}

body.dark-mode .donation-button-style:disabled {
    background: #555;
    color: #999;
}

.restore-button {
    background-color: transparent;
    color: #666;
    border: 1px solid #ccc;
    padding: 10px 20px;
    border-radius: 25px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: underline;
}

.restore-button:hover:not(:disabled) {
    color: #333;
    border-color: #999;
}

.restore-button:active:not(:disabled) {
    transform: scale(0.95);
}

.restore-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.restore-message {
    font-size: 0.9em;
    margin-top: 10px;
    padding: 10px;
    border-radius: 8px;
    max-width: 400px;
}

.restore-success {
    color: #44aa44;
    background-color: rgba(68, 170, 68, 0.1);
}

.restore-error {
    color: #ff4444;
    background-color: rgba(255, 68, 68, 0.1);
}

body.dark-mode .restore-button {
    color: #aaa;
    border-color: #555;
}

body.dark-mode .restore-button:hover:not(:disabled) {
    color: #fff;
    border-color: #777;
}

body.dark-mode .restore-success {
    color: #66cc66;
    background-color: rgba(68, 170, 68, 0.2);
}

body.dark-mode .restore-error {
    color: #ff6666;
    background-color: rgba(255, 68, 68, 0.2);
}
</style>
